import AddTaskIcon from '@mui/icons-material/AddTask';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { IIssueWorkLog, ITimesheetProjectApproval, ITimesheetTaskRow } from '@pulse/shared';
import { Box, ICON_SIZE, IconButton, LabelWithIcon, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@pulse/ui';
import { Fragment, useState } from 'react';

import { LINE_CLAMP_2_SX, TruncatableText } from '../../../components/TruncatableText';
import { cellTotal, dayColumnTotal, formatTotalHours, groupTaskRowsByProject, parseLocalDate, projectDayTotal, projectTotal, taskRowTotal, weekTotal } from '../utils/timesheetGrid';

import { TimesheetApprovalIndicator } from './TimesheetApprovalIndicator';

interface TimesheetGridProps {
  taskRows: ITimesheetTaskRow[];
  workLogs: IIssueWorkLog[];
  days: string[];
  selectedCell: { issueId: string; dateIso: string } | null;
  onCellClick: (issueId: string, dateIso: string) => void;
  /** Per-project approval status for this week (see ITimesheetWeekResponse.projectApprovals) —
   *  drives the Submitted/Approved/Rejected indicator on each project's group header. A project
   *  absent from this list is Pending (no indicator, shows a per-project Submit button). */
  projectApprovals?: ITimesheetProjectApproval[];
  /** Submits a single project's week — omitted (e.g. read-only approver drilldown) hides the
   *  per-project Submit button entirely. */
  onSubmitProject?: (projectId: string) => void;
  isSubmitting?: boolean;
}

function dayLabel(dateIso: string): string {
  return parseLocalDate(dateIso).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
}

// Vertical axis: tasks grouped by project (project header row + task sub-rows, collapsible, with
// a per-project subtotal row), horizontal axis: the 7 days of the displayed week, cell = total
// hours logged for that (task, day) — clicking a cell (including an empty one, to add a first
// entry) selects it for the side panel.
export function TimesheetGrid({
  taskRows,
  workLogs,
  days,
  selectedCell,
  onCellClick,
  projectApprovals = [],
  onSubmitProject,
  isSubmitting,
}: TimesheetGridProps) {
  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(new Set());

  const projectGroups = groupTaskRowsByProject(taskRows);

  const approvalByProject = new Map(projectApprovals.map((a) => [a.projectId, a]));

  if (taskRows.length === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No tasks assigned or logged this week.</Typography>;
  }

  function toggleCollapsed(projectId: string) {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 720 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>
              <LabelWithIcon icon={<AddTaskIcon fontSize={ICON_SIZE.sm} />} label="Task" />
            </TableCell>
            {days.map((d) => (
              <TableCell key={d} align="center" sx={{ fontWeight: 700 }}>{dayLabel(d)}</TableCell>
            ))}
            <TableCell align="center" sx={{ fontWeight: 700 }}>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {projectGroups.map((group) => {
            const projectId = group.projectId;
            const isCollapsed = collapsedProjects.has(projectId);
            const approval = approvalByProject.get(projectId);
            const issueIds = group.tasks.map((t) => t.issueId);
            return (
              <Fragment key={projectId}>
                <TableRow>
                  <TableCell colSpan={days.length + 2} sx={{ bgcolor: 'action.hover', fontWeight: 700, py: 0.5 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <IconButton size="small" onClick={() => toggleCollapsed(projectId)} aria-label={isCollapsed ? 'Expand' : 'Collapse'}>
                        {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                      </IconButton>
                      <span>{group.projectName}</span>
                      <TimesheetApprovalIndicator approval={approval} projectId={projectId} onSubmitProject={onSubmitProject} isSubmitting={isSubmitting} />
                    </Stack>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Subtotal</TableCell>
                  {days.map((d) => (
                    <TableCell key={d} align="center" sx={{ color: 'text.secondary' }}>
                      {projectDayTotal(workLogs, issueIds, d) ? formatTotalHours(projectDayTotal(workLogs, issueIds, d)) : '—'}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary' }}>{formatTotalHours(projectTotal(workLogs, issueIds))}</TableCell>
                </TableRow>

                {!isCollapsed && group.tasks.map((task) => (
                  <TableRow key={task.issueId} hover>
                    <TableCell sx={{ pl: 4, maxWidth: 320 }}>
                      <TruncatableText
                        text={`${task.issueKey} — ${task.issueName}`}
                        variant="body2"
                        placement="top"
                        sx={LINE_CLAMP_2_SX}
                      />
                    </TableCell>
                    {days.map((d) => {
                      const hours = cellTotal(workLogs, task.issueId, d);
                      const isSelected = selectedCell?.issueId === task.issueId && selectedCell?.dateIso === d;
                      return (
                        <TableCell key={d} align="center" sx={{ p: 0.5 }}>
                          <Box
                            onClick={() => onCellClick(task.issueId, d)}
                            sx={{
                              cursor: 'pointer',
                              borderRadius: 1,
                              py: 0.5,
                              bgcolor: isSelected ? 'primary.main' : hours > 0 ? 'action.selected' : 'transparent',
                              color: isSelected ? 'primary.contrastText' : 'text.primary',
                              '&:hover': { bgcolor: isSelected ? 'primary.main' : 'action.hover' },
                            }}
                          >
                            <Typography variant="body2">{hours > 0 ? hours : '—'}</Typography>
                          </Box>
                        </TableCell>
                      );
                    })}
                    <TableCell align="center" sx={{ fontWeight: 600 }}>{formatTotalHours(taskRowTotal(workLogs, task.issueId))}</TableCell>
                  </TableRow>
                ))}
              </Fragment>
            );
          })}
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Day Totals</TableCell>
            {days.map((d) => (
              <TableCell key={d} align="center" sx={{ fontWeight: 700 }}>{formatTotalHours(dayColumnTotal(workLogs, d))}</TableCell>
            ))}
            <TableCell align="center" sx={{ fontWeight: 700 }}>{formatTotalHours(weekTotal(workLogs))}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
